import React, { Fragment, useMemo } from 'react';
import { CumulativeReturnSnapshotChart, PercentageSpan } from 'venn-components';
import type { SearchResultWithUIState } from './types';
import { renderExtrapolation, renderProxyType, renderTimespan } from './Shared';
import { get10YearRet, getCumRet, isOwnedBySystemUser } from './utils';
import { Dates, getProxyEndDate, getProxyStartDate, getProxyTypeNoun, useHasFF } from 'venn-utils';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContentWithArrow, TooltipTrigger } from '@/components/ui/tooltip';
import type { ProxyInfo } from 'venn-api';

export const CUMULATIVE_TOOLTIP_BACKGROUND = 'rgba(16, 22, 27, 0.9)';

const renderFullHistoryReturn = (item: SearchResultWithUIState) => {
  const value = getCumRet(item);
  if (value === 0 || value) {
    return (
      <p>
        Cum. Return*&nbsp;
        <PercentageSpan className="tooltip-value" value={value} />
      </p>
    );
  }
  return null;
};

const render10YearReturn = (item: SearchResultWithUIState) => {
  const value = get10YearRet(item);
  if (value === 0 || value) {
    return (
      <p>
        Cum. Return (10yr)*&nbsp;
        <PercentageSpan className="tooltip-value" value={value} />
      </p>
    );
  }
  return null;
};

interface CumulativeTooltipProps {
  item: SearchResultWithUIState;
  min: number;
  max: number;
}
const CumulativeTooltip = ({ item, min, max }: CumulativeTooltipProps) => {
  const cumulativeTooltipContent = useCumulativeTooltipContent({ item });
  return (
    <StyledCumulativeTooltip content={cumulativeTooltipContent} position="top" maxWidth={300}>
      <span>
        <CumulativeReturnSnapshotChart
          cumulativeReturn={item.tenYearCumulativeReturnSeries as [number, number][]}
          highlightFirstPoint
          max={max}
          min={min}
          proxyStart={item.proxyStartRange}
          proxyEnd={item.proxyEndRange}
          extrapolateStart={item.extrapolated ? item.unproxiedEndRange : undefined}
        />
      </span>
    </StyledCumulativeTooltip>
  );
};

const areEqual = (prevProps: CumulativeTooltipProps, nextProps: CumulativeTooltipProps) =>
  prevProps.item.tenYearCumulativeReturnSeries === nextProps.item.tenYearCumulativeReturnSeries;
export default React.memo(CumulativeTooltip, areEqual);

interface StyledCumulativeTooltipProps {
  children: React.ReactNode;
  content?: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: number;
  background?: string;
}

export const StyledCumulativeTooltip = ({
  children,
  content,
  position = 'top',
  maxWidth = 300,
  background,
}: StyledCumulativeTooltipProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContentWithArrow
        side={position ?? 'top'}
        align="center"
        collisionPadding={24}
        sideOffset={-6}
        className="bg-venn-black bg-opacity-90 text-white overflow-visible"
        style={{ maxWidth, background }}
      >
        <div className="w-full min-w-[240px] break-words whitespace-normal text-[12px] [&_h1]:text-[1.5em] [&>p]:m-0 [&>.tooltip-value]:float-right [&>.tooltip-value]:font-bold [&>.tooltip-value]:pl-[13px]">
          {content}
        </div>
      </TooltipContentWithArrow>
    </Tooltip>
  );
};

const useCumulativeTooltipContent = ({ item }: { item: SearchResultWithUIState }) => {
  const hasLayeredProxyPickerFF = useHasFF('layered_proxy_picker_ff');
  return useMemo(() => {
    return hasLayeredProxyPickerFF ? (
      <TooltipContent tooltipGroups={getTooltipRowGroups(item)} title={item.name} />
    ) : (
      <div className="min-w-[240px] space-y-2 [&>p]:flex [&>p]:justify-between [&>p>span]:font-bold">
        <h1 className="leading-[1.1]">{item.name} Cumulative Return</h1>
        {item.portfolioId && renderTimespan('Availability', item.startRange, item.endRange, item.frequency)}
        {item.fundId &&
          renderTimespan('Availability', item.unproxiedStartRange, item.unproxiedEndRange, item.unproxiedFrequency)}
        {renderProxyType(item.proxyType)}
        {item.proxyType !== 'EXTRAPOLATE' && renderExtrapolation(item.extrapolated ?? false)}
        {renderTimespan('Proxy Range', item.proxyStartRange, item.proxyEndRange, item.frequency, true)}
        {render10YearReturn(item)}
        {renderFullHistoryReturn(item)}
      </div>
    );
  }, [item, hasLayeredProxyPickerFF]);
};

interface TooltipRow {
  title: string;
  value: React.ReactNode;
  key: string;
}
const getTooltipRowGroups = (item: SearchResultWithUIState): TooltipRow[][] => {
  const getAvailabilityTooltipRows = (): TooltipRow[] => {
    const rows: TooltipRow[] = [];
    if (item.fundId) {
      const { unproxiedStartRange, unproxiedEndRange, unproxiedFrequency } = item;
      const startYear = Dates.toDDMMMYYYY(unproxiedStartRange, unproxiedFrequency);
      const endYear = Dates.toDDMMMYYYY(unproxiedEndRange, unproxiedFrequency);
      rows.push({
        title: 'Availability',
        value: <strong>{startYear !== endYear ? `${startYear} - ${endYear}` : startYear}</strong>,
        key: `availability-fund-${item.fundId}`,
      });
    }
    if (item.portfolioId) {
      const { startRange, endRange, frequency } = item;
      const startYear = Dates.toDDMMMYYYY(startRange, frequency);
      const endYear = Dates.toDDMMMYYYY(endRange, frequency);
      rows.push({
        title: 'Availability',
        value: <strong>{startYear !== endYear ? `${startYear} - ${endYear}` : startYear}</strong>,
        key: `availability-portfolio-${item.portfolioId}`,
      });
    }
    return rows;
  };

  const getReturnsProxyTooltipRows = (proxyInfo?: ProxyInfo): TooltipRow[] => {
    const rows: TooltipRow[] = [];
    if (!proxyInfo) {
      return rows;
    }
    const frequency = item.frequency;
    const extrapolate = proxyInfo.proxyType !== 'EXTRAPOLATE' ? proxyInfo.extrapolate : false;
    const { proxyName, proxyType, proxyNumLags } = proxyInfo;

    if (proxyName) {
      rows.push({
        title: 'Returns Proxy',
        value: <strong>{proxyName}</strong>,
        key: `returns-proxy-${proxyInfo.proxyId}`,
      });
    }
    if (proxyType) {
      rows.push({
        title: 'Proxy Type',
        value: <span>{getProxyTypeNoun(proxyType)}</span>,
        key: `proxy-type-${proxyInfo.proxyId}`,
      });
    }
    if (item.proxyStartRange && item.proxyEndRange && frequency && !isOwnedBySystemUser(item.owner)) {
      const startYear = Dates.toDDMMMYYYY(
        proxyInfo.proxyType === 'EXTRAPOLATE'
          ? getProxyStartDate({
              proxyType: 'EXTRAPOLATE',
              proxiedStartDate: item.proxyStartRange,
              unproxiedEndDate: item.unproxiedEndRange,
              proxiedFrequency: frequency,
            })
          : proxyInfo.proxyStartRange,
        item.frequency,
      );
      const endYear = Dates.toDDMMMYYYY(
        ['BACKFILL', 'EXTRAPOLATE'].includes(proxyInfo.proxyType)
          ? getProxyEndDate({
              proxyType: proxyInfo.proxyType,
              extrapolate,
              unproxiedEndDate: item.unproxiedEndRange,
              proxiedStartDate: item.startRange,
              unproxiedStartDate: item.unproxiedStartRange,
              proxiedFrequency: frequency,
              proxiedEndDate: item.proxyEndRange,
            })
          : proxyInfo.proxyEndRange,
        item.frequency,
      );
      rows.push({
        title: 'Proxy Range',
        value: (
          <span className="text-venn-pale-gold">{startYear !== endYear ? `${startYear} - ${endYear}` : startYear}</span>
        ),
        key: `proxy-range-${proxyInfo.proxyId}`,
      });
    }
    if (proxyNumLags) {
      rows.push({ title: 'Lag', value: <span>{proxyNumLags}</span>, key: `lag-${proxyInfo.proxyId}` });
    }

    if (extrapolate) {
      rows.push({ title: 'Proxy Type', value: 'Extrapolate', key: `proxy-type-extrapolate-${proxyInfo.proxyId}` });
      if (
        proxyInfo.extrapolateStartRange &&
        proxyInfo.extrapolateEndRange &&
        frequency &&
        !isOwnedBySystemUser(item.owner)
      ) {
        const startYear = Dates.toDDMMMYYYY(proxyInfo.extrapolateStartRange, frequency);
        const endYear = Dates.toDDMMMYYYY(proxyInfo.extrapolateEndRange, frequency);
        rows.push({
          title: 'Proxy Range',
          value: (
            <span className="text-venn-pale-gold">
              {startYear !== endYear ? `${startYear} - ${endYear}` : startYear}
            </span>
          ),
          key: `proxy-range-extrapolate-${proxyInfo.proxyId}`,
        });
      }
    }
    return rows;
  };

  const getCumReturnRows = () => {
    const rows: TooltipRow[] = [];
    const cumReturn = getCumRet(item);
    if (cumReturn === 0 || cumReturn) {
      rows.push({
        title: 'Cum. Return*',
        value: <PercentageSpan value={cumReturn} className="font-bold" />,
        key: 'cum-return',
      });
    }
    const _10YearRet = get10YearRet(item);
    if (_10YearRet === 0 || _10YearRet) {
      rows.push({
        title: 'Cum. Return (10yr)*',
        value: <PercentageSpan value={_10YearRet} className="font-bold" />,
        key: '10-year-cum-return',
      });
    }
    return rows;
  };

  const availabilityTooltipRows = getAvailabilityTooltipRows();
  const returnsProxyInfos = item.proxyInfos?.filter((proxyInfo) => proxyInfo.proxyVariant === 'RETURN');
  const firstProxyTooltipRows = getReturnsProxyTooltipRows(returnsProxyInfos?.[0]);
  const secondProxyTooltipRows = getReturnsProxyTooltipRows(returnsProxyInfos?.[1]);
  const cumReturnRows = getCumReturnRows();

  return [availabilityTooltipRows, firstProxyTooltipRows, secondProxyTooltipRows, cumReturnRows].filter(
    (group) => group.length > 0,
  );
};

interface TooltipContentProps {
  title: string;
  tooltipGroups: TooltipRow[][];
}

const TooltipContent = ({ title, tooltipGroups }: TooltipContentProps) => (
  <div className="min-w-[240px]">
    <h1 className="leading-[1.1]">{title} Cumulative Return</h1>
    {tooltipGroups.map((group, groupIndex) =>
      group.map((row, rowIndex) => (
        <Fragment key={row.key}>
          <div className="flex flex-row justify-between p-[3px]">
            <span className="whitespace-nowrap">{row.title}</span>
            <span className="text-right pl-[13px]">{row.value}</span>
          </div>
          {rowIndex === group.length - 1 && groupIndex !== tooltipGroups.length - 1 && (
            <Separator className="bg-venn-grey-60 my-[4px]" />
          )}
        </Fragment>
      )),
    )}
  </div>
);
